// src/components/common/WhatsAppSupportButton.tsx

import React, { useState } from 'react';
import { Box, Fab, Tooltip, Zoom, Typography } from '@mui/material';
import { WhatsApp } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

interface WhatsAppSupportButtonProps {
  phoneNumber?: string;
  message?: string;
}

// Routes where WhatsApp button should be hidden (public customer-facing pages)
const HIDDEN_ROUTES = [
  '/',           // Home page
  '/menu',       // Menu page (prefix match)
  '/cart',       // Cart page
  '/checkout',   // Checkout page
  '/order-success', // Order success page
  '/track-order',   // Track order page
  '/restaurant/cashier', // Cashier POS page
];

const WhatsAppSupportButton: React.FC<WhatsAppSupportButtonProps> = ({
  phoneNumber = '9647777713538', // Iraqi number without leading 0
  message,
}) => {
  const { t } = useLanguage();
  const location = useLocation();
  const defaultMessage = message || t('whatsapp.support');
  const [isHovered, setIsHovered] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Global detector for any MUI Drawer or Modal being open
  React.useEffect(() => {
    const checkModals = () => {
      // MUI temporary drawers and dialogs add 'MuiModal-root' elements to the body.
      // However, some might be persistent but hidden (like the mobile sidebar).
      // We check for modals that are currently visible and NOT marked as hidden by MUI.
      const modals = document.querySelectorAll('.MuiModal-root');
      const hasVisibleModal = Array.from(modals).some(modal => {
        const isHidden = modal.classList.contains('MuiModal-hidden') ||
          window.getComputedStyle(modal).visibility === 'hidden' ||
          window.getComputedStyle(modal).display === 'none';
        return !isHidden;
      });

      setIsDrawerOpen(hasVisibleModal);
    };

    // Initial check
    checkModals();

    // Use MutationObserver to watch for changes in the DOM (MUI adds/removes modals)
    const observer = new MutationObserver(() => {
      checkModals();
    });

    observer.observe(document.body, {
      childList: true,
      attributes: true,
      subtree: false // MUI Modals are usually direct children of body
    });

    return () => observer.disconnect();
  }, []);

  // Check if current route should hide the WhatsApp button
  const shouldHide = HIDDEN_ROUTES.some(route => {
    if (route === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(route);
  });

  // Don't render on public customer-facing pages or when a drawer is open
  if (shouldHide || isDrawerOpen) {
    return null;
  }

  const handleClick = () => {
    const encodedMessage = encodeURIComponent(defaultMessage);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 24, sm: 32 },
        left: { xs: 24, sm: 32 },
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Label - shows on hover */}
      <Zoom in={isHovered}>
        <Box
          sx={{
            bgcolor: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Cairo, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#1a1a1a',
              whiteSpace: 'nowrap',
            }}
          >
            {t('whatsapp.support')}
          </Typography>
        </Box>
      </Zoom>

      {/* WhatsApp Button */}
      <Tooltip
        title={t('whatsapp.support')}
        placement="top"
        arrow
      >
        <Fab
          onClick={handleClick}
          sx={{
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            bgcolor: '#25D366',
            color: 'white',
            boxShadow: '0 6px 24px rgba(37, 211, 102, 0.4)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: '#128C7E',
              transform: 'scale(1.1)',
              boxShadow: '0 8px 32px rgba(37, 211, 102, 0.5)',
            },
            // Pulse animation
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(37, 211, 102, 0.4)',
              },
              '70%': {
                boxShadow: '0 0 0 15px rgba(37, 211, 102, 0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(37, 211, 102, 0)',
              },
            },
          }}
        >
          <WhatsApp sx={{ fontSize: { xs: 28, sm: 32 } }} />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default WhatsAppSupportButton;
